/**
 * Storage layer — localStorage wrapper.
 *
 * ───────────────────────────────────────────────────────────────────────────
 * FUTURE: Swapping to a real backend (Supabase recommended)
 * ───────────────────────────────────────────────────────────────────────────
 * The exported API (`load`, `save`) is the only contract the rest of the app
 * relies on. To migrate to Supabase or any backend, replace the bodies of
 * `load` and `save` without changing their signatures.
 *
 * Example Supabase swap:
 *
 *   import { supabase } from "./supabaseClient";
 *
 *   export async function load() {
 *     const userId = (await supabase.auth.getUser()).data.user.id;
 *     const [{ data: clients }, { data: offers }, { data: payments }] =
 *       await Promise.all([
 *         supabase.from("clients").select("*").eq("owner_id", userId),
 *         supabase.from("offers").select("*").eq("owner_id", userId),
 *         supabase.from("payments").select("*").eq("owner_id", userId),
 *       ]);
 *     return { clients, offers, payments, settings: defaults() };
 *   }
 *
 *   export async function save(state) {
 *     // upsert each table — or better, mutate per-record where you call it.
 *     await supabase.from("clients").upsert(state.clients);
 *     await supabase.from("offers").upsert(state.offers);
 *     await supabase.from("payments").upsert(state.payments);
 *   }
 *
 * `useDashboard` already awaits these (treats them as async-safe).
 * ───────────────────────────────────────────────────────────────────────────
 */

const KEY = "unicamel.agency.dashboard.v1";

export const defaults = () => ({
  clients: [],
  offers: [
    {
      id: "offer-dwy",
      name: "Done With You Monthly",
      type: "recurring",
      defaultAmount: 2500,
      description: "Monthly retainer for representation, outreach, and pipeline management.",
    },
    {
      id: "offer-dfy",
      name: "Done For You Setup",
      type: "oneoff",
      defaultAmount: 5000,
      description: "One-off setup engagement to build the full outbound system.",
    },
  ],
  payments: [],
  team: [],
  // Tool: { id, name, category, unitLabel, status: "active"|"inactive",
  //         type: "recurring"|"variable", notes,
  //         unitsByMonth: { "YYYY-MM": number }, priceByMonth: { "YYYY-MM": number } }
  tools: [],
  // ToolTransaction: { id, toolId, date, usdAmount, nativeAmount,
  //                    nativeCurrency, fxRate, notes }
  // One row per top-up / wallet refill / variable purchase for a
  // type='variable' tool. Discrete entries, not a matrix.
  toolTransactions: [],
  // OneOffPayment: { id, name, category, date, amount, notes }
  oneOffPayments: [],
  // Dividend: { id, date, department, recipient, amount, paymentMethod, notes }
  // Money paid OUT to Ahmed / partners — attributed to a department so
  // per-department Cash in Bank subtracts correctly. Manually logged.
  dividends: [],
  // Vacation: { id, memberId, startDate, endDate, notes }
  // Unpaid leave that subtracts days from the member's prorated salary.
  vacations: [],
  // ContractorPayment: { id, contractorId, date, usdAmount, nativeAmount,
  //                      nativeCurrency, fxRate, notes }
  // One row per actual payment to a contractor. Unlike employees, who have
  // a recurring monthlySalary, contractors are paid per transaction.
  contractorPayments: [],
  // PartnerRule: { id, partnerId, basis: "revenue"|"net_profit",
  //                scope: "department"|"offer"|"agency", scopeId,
  //                percent, startsOn, endsOn, notes }
  // Each row defines a partner's automatic cut. Combined with payments +
  // P&L, this produces "cut owed" each month per partner.
  partnerRules: [],
  fxRates: [], // { month: "2026-05", usdToEgp: 47.5 }
  settings: {
    baseCurrency: "USD",
    matrixMonthsAhead: 18,
    duePresets: [7, 14, 30, 60],
    darkMode: false,
    acquisitionSources: ["Cold email", "Referral", "Inbound", "Facebook ads", "Google ads", "Other"],
    funnels: [],
    clientTypes: ["Hotel", "Resort", "DMC", "Tour operator", "Other"],
    departments: ["SEO", "Paid Ads", "Cold Email", "Shared"],
    // GHL invoice number → offer id override, so live-synced GHL payments can
    // be re-tagged to clean offers without ever touching the GHL source.
    ghlInvoiceOfferMap: {},
  },
});

export function load() {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return defaults();
    const parsed = JSON.parse(raw);
    return {
      ...defaults(),
      ...parsed,
      settings: { ...defaults().settings, ...(parsed.settings || {}) },
    };
  } catch (e) {
    console.error("storage.load failed, returning defaults", e);
    return defaults();
  }
}

export function save(state) {
  try {
    localStorage.setItem(KEY, JSON.stringify(state));
  } catch (e) {
    console.error("storage.save failed", e);
  }
}

export function clearAll() {
  localStorage.removeItem(KEY);
}

export function exportJSON(state) {
  return JSON.stringify(state, null, 2);
}

export function importJSON(text) {
  const parsed = JSON.parse(text);
  return {
    ...defaults(),
    ...parsed,
    settings: { ...defaults().settings, ...(parsed.settings || {}) },
  };
}